import { FC, memo, ReactNode } from 'react';
import { Flex, FlexProps } from 'antd';
import { FormLabel } from './FormLabel';
import {
  FormInputMessages,
  FormInput,
  FormInputPreview,
  FormInputPreviewProps,
} from './input';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { useGetIdFromName } from './utils';

export interface FormFieldProps extends Omit<FlexProps, 'children'> {
  name: string;
  id?: string;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  deps?: string | string[];
  preview?: boolean | Omit<FormInputPreviewProps, 'name' | 'id'>;
  size?: SizeType;
  children?: ReactNode;
  hiddenLabel?: boolean;
  showValidationMessages?: boolean;
  showCount?: boolean;
  maxLength?: number;
}

export const FormField: FC<FormFieldProps> = memo((props) => {
  const getIdFromName = useGetIdFromName();
  const {
    name,
    id = getIdFromName(name),
    label,
    placeholder,
    disabled,
    required,
    deps,
    showCount,
    maxLength,
    children = (
      <FormInput
        name={name}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        deps={deps}
        showCount={showCount}
        maxLength={maxLength}
      />
    ),
    preview = false,
    size = 'middle',
    className,
    hiddenLabel = false,
    showValidationMessages = true,
    ...rest
  } = props;

  const classNames = ['form-field'];

  if (className) {
    classNames.push(className);
  }

  return (
    <Flex
      vertical
      gap={hiddenLabel ? undefined : 'small'}
      className={classNames.join(' ')}
      {...rest}
    >
      {label && (
        <FormLabel htmlFor={id} required={required} hidden={hiddenLabel}>
          {label}
        </FormLabel>
      )}
      <FormInputPreview
        {...(typeof preview === 'object'
          ? { enabled: true, ...preview }
          : { enabled: Boolean(preview) })}
        size={size}
        name={name}
        id={id}
        disabled={disabled}
      >
        {children}
      </FormInputPreview>
      {showValidationMessages && <FormInputMessages name={name} />}
    </Flex>
  );
});
